import type { CashAccountBalance, CashLedgerRow, CashPosition } from '../api/cash';
import type { CashSourceKind } from '../generated/enums';

/**
 * The pure half of the cash screen.
 *
 * <p>
 * <strong>Most of what is here is about what the figure may be called</strong>, which is the thing
 * this module has been careful about since the ledger shipped with no reader at all. A balance built
 * from takings alone overstates by everything the shop spends; one built without a float is net
 * movement since the ledger began rather than what is in the drawer. The first is closed. The second
 * is a per-account fact the server reports, and every one of these functions exists so no screen
 * quietly rounds it off into "cash in hand".
 * </p>
 */

/** What the headline figure is honestly called, given what the shop has told us. */
export type PositionConfidence = 'balance' | 'movement';

export function confidenceOf(position: CashPosition | undefined): PositionConfidence {
  // Absent is treated as unproven rather than proven — the screen is not allowed to claim more
  // while it is still loading than it will once the answer arrives.
  if (!position || position.anyMissingOpeningBalance) {
    return 'movement';
  }

  return 'balance';
}

/**
 * The heading over the total.
 *
 * <p>
 * Two different sentences rather than one with a caveat appended, because a caveat under a big
 * number is read as decoration. "Cash in hand" is a claim; "money in less money out" is a
 * description, and the second is all that is true until every account has a float.
 * </p>
 */
export function headlineLabel(confidence: PositionConfidence): string {
  return confidence === 'balance' ? 'Cash in hand' : 'Money in, less money out';
}

/**
 * The one sentence a screen prints when the figure is not yet a balance — or null when it is.
 *
 * <p>
 * Names the accounts, because "some account" is not something a shop can act on, and the action is
 * per account: each needs its own float recorded once.
 * </p>
 *
 * <p>
 * <strong>The predicate is the server's, restated exactly.</strong> `anyMissingOpeningBalance` is
 * `!hasOpeningBalance && (isActive || closing !== 0)` — a retired account holding nothing is a
 * closed account rather than a missing float, and counting it would leave the headline permanently
 * unable to say "cash in hand" with no remedy, since a retired account refuses the posting a float
 * is. Filtering on `hasOpeningBalance` alone named such an account in this sentence while the
 * server had already excluded it from the flag above, so the notice listed a shop an account it
 * could do nothing about and which its own card no longer flagged.
 * </p>
 */
export function openingBalanceNotice(position: CashPosition | undefined): string | null {
  if (!position || !position.anyMissingOpeningBalance) {
    return null;
  }

  const missing = position.accounts.filter(missesOpeningBalance);

  if (missing.length === 0) {
    // The flag said yes and no account agrees. Reported rather than guessed at: silently printing
    // nothing would leave the headline claiming more than the server allowed.
    return 'One account has no opening balance recorded.';
  }

  const names = missing.map((a) => a.name).join(', ');

  return missing.length === 1
    ? `${names} has no opening balance, so this is what has moved rather than what is in it.`
    : `${names} have no opening balance, so this is what has moved rather than what is in them.`;
}

/**
 * Whether an account is one of the reasons the total is not yet a balance.
 *
 * <p>
 * The single definition of the rule, so the sentence and the flag on each card cannot disagree —
 * and it is deliberately the same shape as the server's, which is the authority. A retired account
 * still holding money counts, because its balance is in the total and the total is only as honest
 * as it; one holding nothing does not, because it is closed rather than unfinished.
 * </p>
 */
export function missesOpeningBalance(account: CashAccountBalance): boolean {
  return !account.hasOpeningBalance && (account.isActive || account.closing !== 0);
}

/**
 * Accounts that still need a float, which is what the screen offers a button against.
 *
 * <p>
 * <strong>Retired accounts are left out, because the server refuses a posting to one</strong> — and
 * a float is a posting. The notice above may still name such an account when it holds money, which
 * is right: its balance is in the total, so the total is only as honest as it is. The remedy is to
 * bring the account back, record the float and retire it again, rather than a form that would 400.
 * </p>
 */
export function accountsNeedingOpeningBalance(
  position: CashPosition | undefined,
): CashAccountBalance[] {
  return position?.accounts.filter((a) => !a.hasOpeningBalance && a.isActive) ?? [];
}

/**
 * What a ledger row is called on screen.
 *
 * <p>
 * A `Record<CashSourceKind, string>` rather than a lookup with a fallback: a new source kind is then
 * a compile error here instead of a blank cell, which is the two-guard rule this codebase applies to
 * every wording map over a generated enum. `position.test.ts` asserts none of them is empty, since
 * `''` satisfies the type and renders nothing.
 * </p>
 */
export const sourceLabels: Record<CashSourceKind, string> = {
  RentalPayment: 'Booking',
  Expense: 'Expense',
  VendorPayment: 'Vendor payment',
  Transfer: 'Money moved',
  Opening: 'Opening balance',
  Adjustment: 'Adjustment',
  CashVoucher: 'Voucher',
  SalePayment: 'Sale',
};

/**
 * How a row's amount reads: what it did to the drawer, not what sign it carries.
 *
 * The ledger stores the direction as a column and the amount unsigned, deliberately — so the sign
 * is put back on here, once, rather than by each screen that renders a figure.
 */
export function signedAmount(row: CashLedgerRow): number {
  return row.direction === 'In' ? row.amount : -row.amount;
}

/** The windows the cash book offers. `all` is no window at all — the whole history. */
export type CashPeriod = 'all' | 'today' | 'month' | 'lastMonth';

export const periodLabels: Record<CashPeriod, string> = {
  all: 'All time',
  today: 'Today',
  month: 'This month',
  lastMonth: 'Last month',
};

/**
 * A period as the two dates the API takes.
 *
 * <p>
 * <strong>Computed here rather than by dayjs's `startOf('month')`, and by hand for the reason the
 * morning screen computes its week by hand</strong> — that function reads the locale, so the answer
 * changes with the browser rather than with the shop. A month is unambiguous everywhere, but the
 * habit is what matters: the boundary of a reporting window must not be a setting nobody chose.
 * </p>
 *
 * <p>
 * Both ends are inclusive, matching the server, which is also the rental-date convention: "1 to 31
 * March" is the whole month.
 * </p>
 */
export function periodRange(
  period: CashPeriod,
  today: Date,
): { from: string | undefined; to: string | undefined } {
  if (period === 'all') {
    return { from: undefined, to: undefined };
  }

  const iso = (date: Date) => date.toISOString().slice(0, 10);

  if (period === 'today') {
    const day = iso(today);

    return { from: day, to: day };
  }

  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();
  const offset = period === 'lastMonth' ? -1 : 0;

  // Day 0 of the following month is the last day of this one, which is what keeps February and the
  // thirty-day months right without a table of lengths.
  return {
    from: iso(new Date(Date.UTC(year, month + offset, 1))),
    to: iso(new Date(Date.UTC(year, month + offset + 1, 0))),
  };
}
